import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Flashcard {
  _id: string;
  collectionId: string;
  question: string;
  options: string[];
  correctAnswer: number;
  createdAt: string;
  updatedAt: string;
}

interface FlashcardsState {
  flashcards: Flashcard[];
  activeFlashcard: Flashcard | null;
  isLoading: boolean;
  error: string | null;
  pendingOperations: {
    [key: string]: 'create' | 'update' | 'delete';
  };
}

const initialState: FlashcardsState = {
  flashcards: [],
  activeFlashcard: null,
  isLoading: false,
  error: null,
  pendingOperations: {}
};

export const flashcardsSlice = createSlice({
  name: 'flashcards',
  initialState,
  reducers: {
    setFlashcards: (state, action: PayloadAction<Flashcard[]>) => {
      // Sort by creation date when setting flashcards
      state.flashcards = action.payload.sort((a, b) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      state.error = null;
    },
    
    setActiveFlashcard: (state, action: PayloadAction<Flashcard>) => {
      state.activeFlashcard = action.payload;
      state.error = null;
    },
    
    addFlashcard: (state, action: PayloadAction<Flashcard>) => {
      // Add optimistically
      state.flashcards.push(action.payload);
      state.flashcards.sort((a, b) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      state.pendingOperations[action.payload._id] = 'create';
      state.error = null;
    },

    updateFlashcard: (state, action: PayloadAction<Flashcard>) => {
      const index = state.flashcards.findIndex(f => f._id === action.payload._id);
      if (index !== -1) {
        state.flashcards[index] = action.payload;
      }
      if (state.activeFlashcard?._id === action.payload._id) {
        state.activeFlashcard = action.payload;
      }
      state.pendingOperations[action.payload._id] = 'update';
      state.error = null;
    },

    removeFlashcard: (state, action: PayloadAction<string>) => {
      state.flashcards = state.flashcards.filter(f => f._id !== action.payload);
      if (state.activeFlashcard?._id === action.payload) {
        state.activeFlashcard = null;
      }
      state.pendingOperations[action.payload] = 'delete';
      state.error = null;
    },

    confirmOperation: (state, action: PayloadAction<string>) => {
      delete state.pendingOperations[action.payload];
    },

    revertOperation: (state, action: PayloadAction<{id: string, flashcard?: Flashcard}>) => {
      const operation = state.pendingOperations[action.payload.id];
      if (operation === 'create') {
        state.flashcards = state.flashcards.filter(f => f._id !== action.payload.id);
      } else if (operation === 'update' && action.payload.flashcard) {
        const index = state.flashcards.findIndex(f => f._id === action.payload.id);
        if (index !== -1) {
          state.flashcards[index] = action.payload.flashcard;
        }
      } else if (operation === 'delete' && action.payload.flashcard) {
        state.flashcards.push(action.payload.flashcard);
        state.flashcards.sort((a, b) => 
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      }
      delete state.pendingOperations[action.payload.id];
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    clearFlashcards: (state) => {
      state.flashcards = [];
      state.activeFlashcard = null;
      state.error = null;
      state.pendingOperations = {};
    },
  },
});

export const {
  setFlashcards,
  setActiveFlashcard,
  addFlashcard,
  updateFlashcard,
  removeFlashcard,
  confirmOperation,
  revertOperation,
  setLoading,
  setError,
  clearFlashcards,
} = flashcardsSlice.actions;

export default flashcardsSlice.reducer;